import { Box, Typography, Grid, Chip } from '@mui/material';
import { useAppSelector } from 'store/hook';
import { ThemePalette } from 'mui.theme';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { DATE_FORMAT, TIME_FORMAT } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import type { SquaresAlert } from 'types/squares.type';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface DetailFieldProps {
  label: string;
  fontColor: string;
  children: React.ReactNode;
}

const DetailField = ({ label, fontColor, children }: DetailFieldProps) => (
  <Grid item lg={3}>
    <Typography
      sx={{
        color: ThemePalette.typography.grey,
        fontSize: 12,
        fontWeight: '600',
      }}
    >
      {label}
    </Typography>
    {children}
  </Grid>
);

export const Details = ({
  enableDarkTheme,
  notification,
}: {
  enableDarkTheme: boolean;
  notification: SquaresAlert;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { assets } = useAppSelector((state) => state.assetReducer);
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const fontColor = palette.textPrimary;
  const notificationAsset =
    assets.find((item) => item.bumperNumber === notification.assetName) ??
    assets[0];

  const severityColor =
    notification.severity === 'med'
      ? '#ff9800'
      : notification.severity === 'high'
      ? '#F44336'
      : '#039161';

  const showPlusIcon =
    (notification.notificationName || '').toLowerCase() === 'upcoming-pms' &&
    notification?.data?.length > 0;
  const plusIconCount =
    notification?.data?.reduce((set, item) => {
      set.add(item.asset);
      return set;
    }, new Set()).size || 0;

  return (
    <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
      <DetailField label={t('asset_id')} fontColor={fontColor}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', marginLeft: '-15px' }}
        >
          <PNG
            name={getImageName(notificationAsset, 'Small')}
            alt="ALT"
            style={{ height: '60px', width: '80px' }}
          />
          <Box>
            <Typography
              sx={{ color: fontColor, fontSize: 14, fontWeight: 600 }}
            >
              {showPlusIcon
                ? notification.data[0].asset
                : notification.assetName}
              {showPlusIcon && plusIconCount > 0 && (
                <sup
                  style={{
                    marginLeft: '5px',
                    fontSize: '8px',
                  }}
                >
                  +{plusIconCount - 1} {t('assets')}
                </sup>
              )}
            </Typography>
            <Typography sx={{ color: fontColor, fontSize: 12 }}>
              {notification.makeModel}
            </Typography>
          </Box>
        </Box>
      </DetailField>

      <DetailField label={t('notification_date')} fontColor={fontColor}>
        <Typography sx={{ fontSize: 12, color: fontColor, marginTop: '10px' }}>
          {convertUtcToDisplayTime(
            notification.notifiedDate,
            preferredTimeDisplayMode,
            timezone,
            DATE_FORMAT
          )}
        </Typography>
      </DetailField>

      <DetailField label={t('notification_time')} fontColor={fontColor}>
        <Typography sx={{ fontSize: 12, color: fontColor, marginTop: '10px' }}>
          {convertUtcToDisplayTime(
            notification.notifiedDate,
            preferredTimeDisplayMode,
            timezone,
            TIME_FORMAT
          )}
        </Typography>
      </DetailField>

      <DetailField label={t('severity')} fontColor={fontColor}>
        <Box
          sx={{
            marginTop: '5px',
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <Chip
            label={notification.severity}
            sx={{
              bgcolor: severityColor,
              color: fontColor,
              fontWeight: 600,
              fontSize: 12,
              height: 28,
              borderRadius: 3,
            }}
          />
        </Box>
      </DetailField>
    </Grid>
  );
};
